/**
 * The picker's detail view (MK-4a) — a preview, the provenance, and a REAL
 * variant chooser.
 *
 * What this replaces: a `"4 sizes"` badge that opened a popover of inert
 * `<div>`s with no click handler. It looked like a control, absorbed a click,
 * and could not act. A badge that opens a list must be able to act on it.
 *
 * The chooser is a radio group because the choice is exclusive and one option
 * is always in effect. The best variant is PRE-SELECTED — the widest, then the
 * best format — so a user who does not care never has to decide, which is the
 * other half of "put the chooser where the decision is being made".
 */

import { createButton } from '../../ui/components.js';
import { classifyMedia, normalizeMediaFormat } from '../../shared/media-kind.js';

/** Preference order when two variants are the same width. */
const FORMAT_RANK = { avif: 0, webp: 1, jpg: 2, jpeg: 2, png: 3, gif: 4 };

function rankOf(variant) {
  const fmt = normalizeMediaFormat({ format: variant?.format, filename: variant?.filename });
  return FORMAT_RANK[fmt] ?? 9;
}

/**
 * Widest wins; ties break on format. Exported for the test that pins the
 * default — "best pre-chosen" is a claim, and a claim needs an assertion.
 */
export function bestVariantIndex(variants) {
  let best = -1;
  for (let i = 0; i < (variants?.length || 0); i += 1) {
    if (best === -1) {
      best = i;
      continue;
    }
    const a = variants[i];
    const b = variants[best];
    const aw = Number(a?.width || 0);
    const bw = Number(b?.width || 0);
    if (aw > bw || (aw === bw && rankOf(a) < rankOf(b))) best = i;
  }
  return best;
}

export function variantLabel(variant) {
  const parts = [];
  if (variant?.width) parts.push(`${variant.width}w`);
  else if (variant?.descriptor) parts.push(variant.descriptor);
  const fmt = normalizeMediaFormat({ format: variant?.format, filename: variant?.filename });
  if (fmt) parts.push(fmt.toUpperCase());
  return parts.join(' · ') || variant?.filename || 'Variant';
}

/**
 * @param {object} item — the kit item (its `data` carries the adapter result)
 * @param {{view: HTMLElement, onPick: (item, variant) => void, setActions: (els) => void,
 *   basenameOf?: (url: string) => string}} ctx
 * @returns {() => void} teardown, per the engine's `detail.render` contract
 */
export function renderImageDetail(item, ctx) {
  const { view, onPick, setActions, basenameOf } = ctx;
  const data = item?.data || {};
  const result = data.result || {};
  const raw = result._raw || {};
  const variants = result.variants || raw._variants || [];
  const disabled = !!data.dead;

  const wrap = document.createElement('div');
  wrap.className = 'pb-image-detail';

  // ── the preview ─────────────────────────────────────────────────────────
  const frame = document.createElement('div');
  frame.className = 'pb-image-detail-frame';
  const classification = data.classification || classifyMedia({});
  if (classification.checker) frame.classList.add('pb-checker');
  if (classification.fit === 'contain') frame.classList.add('pb-image-detail-contain');
  const img = document.createElement('img');
  img.src = result.thumb || '';
  img.alt = String(result.title || '');
  img.decoding = 'async';
  frame.appendChild(img);
  wrap.appendChild(frame);

  // ── identity + provenance ───────────────────────────────────────────────
  const heading = document.createElement('h3');
  heading.className = 'pb-image-detail-title';
  heading.textContent = String(raw.filename || result.title || 'Image');
  wrap.appendChild(heading);

  // Provenance lives HERE, not on the card: `extractedFrom` is identical
  // across four consecutive cards, so on a card it discriminates nothing,
  // while here it is the one place with room to say where a thing came from.
  const provenance = String(raw.extractedFrom || result.author || '');
  if (provenance) {
    const from = document.createElement('p');
    from.className = 'pb-image-detail-meta';
    from.textContent = `From ${provenance}`;
    wrap.appendChild(from);
  }

  if (disabled) {
    const offline = document.createElement('p');
    offline.className = 'pb-image-detail-offline';
    offline.textContent =
      "This image's source is offline and no copy was archived, so it cannot be used.";
    wrap.appendChild(offline);
  }

  // ── the variant chooser ─────────────────────────────────────────────────
  let chosenIndex = bestVariantIndex(variants);
  let radios = [];
  if (variants.length > 1) {
    const fieldset = document.createElement('fieldset');
    fieldset.className = 'pb-image-detail-variants';
    const legend = document.createElement('legend');
    legend.textContent = `${variants.length} sizes`;
    fieldset.appendChild(legend);

    // A radio NAME must be unique per dialog, or a second detail view opened
    // in the same document would join the first one's group.
    const groupName = `pb-image-variant-${String(item.id).replace(/[^\w-]/g, '')}`;
    variants.forEach((variant, index) => {
      const row = document.createElement('label');
      row.className = 'pb-image-detail-variant';
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = groupName;
      input.value = String(index);
      input.checked = index === chosenIndex;
      input.disabled = disabled;
      input.addEventListener('change', () => {
        if (input.checked) chosenIndex = index;
      });
      const label = document.createElement('span');
      label.className = 'pb-image-detail-variant-label';
      label.textContent = variantLabel(variant);
      const file = document.createElement('small');
      file.textContent = variant.filename || (basenameOf ? basenameOf(variant.url) : '');
      row.append(input, label, file);
      fieldset.appendChild(row);
      radios.push(input);
    });
    wrap.appendChild(fieldset);
  }

  // ── the actions ─────────────────────────────────────────────────────────
  //
  // Back and Use ride the DIALOG HEADER (collection-browser.js's
  // `mountDetailChrome`), not a row appended after the content. They used to be
  // the last thing in the body, so on a tall detail — a wide variant fieldset,
  // a long attribution — the primary action sat below the fold of a scrolling
  // panel while the thing it acts on filled the screen.
  //
  // The engine owns Back; this supplies only what commits, through
  // `setDetailActions` rather than the declarative `detail.actions`, because
  // `disabled` and the chosen variant are only known once this render has read
  // the item.
  const use = createButton({
    variant: 'primary',
    size: 'sm',
    pill: true,
    label: 'Use this image',
    disabled,
    testid: 'image-detail-use',
    onClick: () => onPick(item, variants[chosenIndex] || null)
  });
  setActions?.([use]);

  view.appendChild(wrap);

  // The engine puts the caret on Back; move it to the primary action when
  // there is one to press, so Enter does the obvious thing.
  if (!disabled) use.focus?.();

  return () => {
    for (const input of radios) input.replaceWith(input.cloneNode(true));
    radios = [];
    wrap.remove();
  };
}
